import React from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { StochasticDashboardFilters } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/DashboardFilters/StochasticDashboardFilters";
import { useStochasticDashboard } from "@/modules/stochastic/features/SalesAndCustomerDashboard/hooks/useStochasticDashboard";
import TotalSalesNewVsExistingCustomerChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesNewVsExistingCustomerChart/TotalSalesNewVsExistingCustomerChart";
import RevenueByInvoiceAgeChart from "../Chart/RevenueByInvoiceAgeChart/RevenueByInvoiceAgeChart";
import TotalSalesNewCustomerByZipCodeAndYearChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesNewCustomerByZipCodeAndYearChart/TotalSalesNewCustomerByZipCodeAndYearChart";
import TotalSalesByYearAndMonthChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesByYearAndMonthChart/TotalSalesByYearAndMonthChart";
import TotalSalesByZipCodeChart from "../Chart/TotalSalesByZipCodeChart/TotalSalesByZipCodeChart";
import { PercentageOfNewCustomersByZipCodeChart } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/PercentageOfNewCustomersByZipCodeChart/PercentageOfNewCustomersByZipCodeChart";
import CustomersAverageInvoiceComparisonChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/CustomersAverageInvoiceComparisonChart/CustomersAverageInvoiceComparisonChart";
import LifetimeValueByTierChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/LifetimeValueByTierChart/LifetimeValueByTierChart";
import { PercentageOfNewCustomersChangeByZipCodeTable } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Table/PercentageOfNewCustomersChangeByZipCodeTable/PercentageOfNewCustomersChangeByZipCodeTable";
import { LifetimeValueByTierTable } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Table/LifetimeValueByTierTable/LifetimeValueByTierTable";
import LifetimeValueByTierChartSkeleton from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/LifetimeValueByTierChart/LifetimeValueByTierChartSkeleton";
import RevenueByInvoiceAgeChartSkeleton from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/RevenueByInvoiceAgeChart/RevenueByInvoiceAgeChartSkeleton";
import { PercentageOfNewCustomersByZipCodeChartSkeleton } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/PercentageOfNewCustomersByZipCodeChart/PercentageOfNewCustomersByZipCodeChartSkeleton";
import { TotalSalesByYearAndMonthChartSkeleton } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesByYearAndMonthChart/TotalSalesByYearAndMonthChartSkeleton";
import { TotalSalesByZipCodeChartSkeleton } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesByZipCodeChart/TotalSalesByZipCodeChartSkeleton";
import TotalSalesNewCustomerByZipCodeAndYearChartSkeleton from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesNewCustomerByZipCodeAndYearChart/TotalSalesNewCustomerByZipCodeAndYearChartSkeleton";
import TotalSalesNewVsExistingCustomerChartSkeleton from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalSalesNewVsExistingCustomerChart/TotalSalesNewVsExistingCustomerChartSkeleton";
import { CustomersAverageInvoiceComparisonChartSkeleton } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/CustomersAverageInvoiceComparisonChart/CustomersAverageInvoiceComparisonChartSkeleton";
import { LifetimeValueByTierTableSkeleton } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Table/LifetimeValueByTierTable/LifetimeValueByTierTableSkeleton";
import { PercentageOfNewCustomersChangeByZipCodeTableSkeleton } from "../Table/PercentageOfNewCustomersChangeByZipCodeTable/PercentageOfNewCustomersChangeByZipCodeTableSkeleton";
import NewCustomerReplacementEfficiencyChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/NewCustomerReplacementEfficiencyChart/NewCustomerReplacementEfficiencyChart";
import NewCustomerReplacementEfficiencyChartSkeleton from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/NewCustomerReplacementEfficiencyChart/NewCustomerReplacementEfficiencyChartSkeleton";
import ExistingCustomerReplacementEfficiencyChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/ExistingCustomerReplacementEfficiencyChart/ExistingCustomerReplacementEfficiencyChart";
import ExistingCustomerReplacementEfficiencyChartSkeleton from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/ExistingCustomerReplacementEfficiencyChart/ExistingCustomerReplacementEfficiencyChartSkeleton";
import TotalHouseholdsByZipCodeChart from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalHouseholdsByZipCodeChart/TotalHouseholdsByZipCodeChart";
import { TotalHouseholdsByZipCodeChartSkeleton } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/Chart/TotalHouseholdsByZipCodeChart/TotalHouseholdsByZipCodeChartSkeleton";
import { LastSyncStatus } from "@/modules/stochastic/features/shared/components/LastSyncStatus/LastSyncStatus";
import { ChartIndex } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/ChartIndex/ChartIndex";

export default function DashboardPage() {
  const {
    dashboardData,
    dashboardFiltersForm,
    loading,
    refreshing,
    activeTab,
    setActiveTab,
    applyFilters,
    areFiltersDirty,
    handleRefresh,
  } = useStochasticDashboard();

  const refreshButton = (
    <div className="flex items-center gap-3">
      <LastSyncStatus lastSync={dashboardData.lastSync} />
      <button
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
        disabled={refreshing}
        onClick={handleRefresh}
      >
        <ArrowPathIcon
          className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
        />
        Refresh
      </button>
    </div>
  );

  const renderSalesCharts = () => (
    <>
      <div id="total-sales-new-vs-existing">
        <TotalSalesNewVsExistingCustomerChart
          initialData={dashboardData.totalSalesNewVsExistingCustomerData || []}
        />
      </div>
      <div id="total-sales-by-year-month">
        <TotalSalesByYearAndMonthChart
          initialData={dashboardData.totalSalesByYearAndMonthData}
        />
      </div>
      <div id="total-sales-by-zip-code">
        <TotalSalesByZipCodeChart
          initialData={dashboardData.totalSalesByZipCodeData || []}
        />
      </div>
      <div id="total-sales-new-customer-by-zip">
        <TotalSalesNewCustomerByZipCodeAndYearChart
          initialData={dashboardData.totalSalesNewCustomerByZipCodeAndYearData}
        />
      </div>
    </>
  );

  const renderSalesChartSkeletons = () => (
    <>
      <TotalSalesNewVsExistingCustomerChartSkeleton />
      <TotalSalesByYearAndMonthChartSkeleton />
      <TotalSalesByZipCodeChartSkeleton />
      <TotalSalesNewCustomerByZipCodeAndYearChartSkeleton />
    </>
  );

  const renderCustomerCharts = () => (
    <>
      <div id="revenue-by-invoice-age">
        <RevenueByInvoiceAgeChart
          initialData={dashboardData.revenueByInvoiceAgeData || []}
        />
      </div>
      <div id="lifetime-value-by-tier">
        <LifetimeValueByTierChart
          initialData={dashboardData.lifetimeValueByTierData}
        />
      </div>
      <div id="lifetime-value-by-tier-table">
        <LifetimeValueByTierTable
          initialData={dashboardData.lifetimeValueByTierData}
        />
      </div>
      <div id="pct-new-customers-by-zip">
        <PercentageOfNewCustomersByZipCodeChart
          initialData={dashboardData.percentageOfNewCustomersByZipCodeData}
        />
      </div>
      <div id="pct-new-customers-change-table">
        <PercentageOfNewCustomersChangeByZipCodeTable
          initialData={
            dashboardData.percentageOfNewCustomersChangeByZipCodeData || []
          }
        />
      </div>
      <div id="avg-invoice-comparison">
        <CustomersAverageInvoiceComparisonChart
          initialData={
            dashboardData.customersAverageInvoiceComparisonData || []
          }
        />
      </div>
      <div id="new-customer-replacement">
        <NewCustomerReplacementEfficiencyChart
          initialData={dashboardData.newCustomerReplacementEfficiencyData || []}
          monthlyData={
            dashboardData.newCustomerReplacementEfficiencyByMonthData
          }
        />
      </div>
      <div id="existing-customer-replacement">
        <ExistingCustomerReplacementEfficiencyChart
          initialData={
            dashboardData.existingCustomerReplacementEfficiencyData || []
          }
          monthlyData={
            dashboardData.existingCustomerReplacementEfficiencyByMonthData
          }
        />
      </div>
    </>
  );

  const renderCustomerSkeletons = () => (
    <>
      <RevenueByInvoiceAgeChartSkeleton />
      <LifetimeValueByTierChartSkeleton />
      <LifetimeValueByTierTableSkeleton />
      <PercentageOfNewCustomersByZipCodeChartSkeleton />
      <PercentageOfNewCustomersChangeByZipCodeTableSkeleton />
      <CustomersAverageInvoiceComparisonChartSkeleton />
      <NewCustomerReplacementEfficiencyChartSkeleton />
      <ExistingCustomerReplacementEfficiencyChartSkeleton />
    </>
  );

  const renderReviewCharts = () => (
    <>
      <div id="total-sales-by-year-month">
        <TotalSalesByYearAndMonthChart
          initialData={dashboardData.totalSalesByYearAndMonthData}
        />
      </div>
      <div id="revenue-by-invoice-age">
        <RevenueByInvoiceAgeChart
          initialData={dashboardData.revenueByInvoiceAgeData || []}
        />
      </div>
      <div id="total-sales-by-zip-code">
        <TotalSalesByZipCodeChart
          initialData={dashboardData.totalSalesByZipCodeData || []}
        />
      </div>
      <div id="total-households-by-zip">
        <TotalHouseholdsByZipCodeChart
          initialData={dashboardData.totalHouseholdsByZipCodeData}
        />
      </div>
      <div id="pct-new-customers-by-zip">
        <PercentageOfNewCustomersByZipCodeChart
          initialData={dashboardData.percentageOfNewCustomersByZipCodeData}
        />
      </div>
      <div id="avg-invoice-comparison">
        <CustomersAverageInvoiceComparisonChart
          initialData={
            dashboardData.customersAverageInvoiceComparisonData || []
          }
        />
      </div>
      <div id="new-customer-replacement">
        <NewCustomerReplacementEfficiencyChart
          initialData={dashboardData.newCustomerReplacementEfficiencyData || []}
          monthlyData={
            dashboardData.newCustomerReplacementEfficiencyByMonthData
          }
        />
      </div>
      <div id="existing-customer-replacement">
        <ExistingCustomerReplacementEfficiencyChart
          initialData={
            dashboardData.existingCustomerReplacementEfficiencyData || []
          }
          monthlyData={
            dashboardData.existingCustomerReplacementEfficiencyByMonthData
          }
        />
      </div>
      <div id="lifetime-value-by-tier">
        <LifetimeValueByTierChart
          initialData={dashboardData.lifetimeValueByTierData}
        />
      </div>
    </>
  );

  const renderReviewChartSkeletons = () => (
    <>
      <TotalSalesByYearAndMonthChartSkeleton />
      <RevenueByInvoiceAgeChartSkeleton />
      <TotalSalesByZipCodeChartSkeleton />
      <TotalHouseholdsByZipCodeChartSkeleton />
      <PercentageOfNewCustomersByZipCodeChartSkeleton />
      <CustomersAverageInvoiceComparisonChartSkeleton />
      <NewCustomerReplacementEfficiencyChartSkeleton />
      <ExistingCustomerReplacementEfficiencyChartSkeleton />
      <LifetimeValueByTierChartSkeleton />
    </>
  );

  const renderChartSkeletons = () => {
    if (activeTab === "sales") return renderSalesChartSkeletons();
    if (activeTab === "customers") return renderCustomerSkeletons();
    return renderReviewChartSkeletons();
  };

  const renderCharts = () => {
    if (activeTab === "sales") return renderSalesCharts();
    if (activeTab === "customers") return renderCustomerCharts();
    return renderReviewCharts();
  };

  return (
    <MainPageWrapper
      actions={refreshButton}
      loading={loading}
      title="Sales and Customer Dashboard"
    >
      <div className="bg-gray-50 dark:bg-slate-950 min-h-screen transition-colors duration-200">
        <StochasticDashboardFilters
          activeTab={activeTab}
          areFiltersDirty={areFiltersDirty}
          chartFiltersForm={dashboardFiltersForm}
          onApplyFilters={applyFilters}
          setActiveTab={setActiveTab}
        />
        <div className="flex gap-6 pt-2">
          <ChartIndex activeTab={activeTab} />
          <div className="flex-1 min-w-0 overflow-hidden space-y-6 [&_.rounded-lg.border]:bg-white dark:[&_.rounded-lg.border]:bg-slate-800">
            {loading ? renderChartSkeletons() : renderCharts()}
          </div>
        </div>
      </div>
    </MainPageWrapper>
  );
}
